import { z } from "zod";

/**
 * Lightweight implementation metadata returned by /api/v4/implementations-meta/*
 */
export const ImplementationMetaSchema = z.object({
  id: z.string().describe("Unique identifier for the implementation"),
  name: z.string().describe("Display name of the implementation"),
  slug: z.string().describe("URL-friendly slug identifier"),
  age_in_days: z
    .number()
    .optional()
    .describe("Number of days since the implementation was created"),
  auth_type: z
    .string()
    .optional()
    .describe("Authentication type (e.g., oauth2, api_key)"),
  banner: z.string().optional().describe("Banner message or status indicator"),
  categories: z
    .array(
      z.object({
        id: z.number().describe("Unique identifier for the category"),
        name: z.string().describe("Display name of the category"),
        slug: z.string().describe("URL-friendly slug for the category"),
      }),
    )
    .optional()
    .describe("Categories the implementation belongs to"),
  images: z
    .object({
      url_16x16: z.string().optional().describe("16x16 pixel icon URL"),
      url_32x32: z.string().optional().describe("32x32 pixel icon URL"),
      url_64x64: z.string().optional().describe("64x64 pixel icon URL"),
      url_128x128: z.string().optional().describe("128x128 pixel icon URL"),
    })
    .optional()
    .describe("Icon images at various sizes"),
  popularity: z
    .number()
    .optional()
    .describe("Popularity score for ranking apps"),
  has_filters: z
    .boolean()
    .optional()
    .describe("Whether the app has filter actions"),
  has_reads: z
    .boolean()
    .optional()
    .describe("Whether the app has read actions"),
  has_searches: z
    .boolean()
    .optional()
    .describe("Whether the app has search actions"),
  has_searches_or_writes: z
    .boolean()
    .optional()
    .describe("Whether the app has search or write actions"),
  has_upfront_fields: z
    .boolean()
    .optional()
    .describe("Whether the app has upfront input fields"),
  has_writes: z
    .boolean()
    .optional()
    .describe("Whether the app has write actions"),
  is_beta: z.boolean().optional().describe("Whether the app is in beta"),
  is_built_in: z
    .boolean()
    .optional()
    .describe("Whether the app is a built-in Zapier app"),
  is_deprecated: z
    .boolean()
    .optional()
    .describe("Whether the app is deprecated"),
  is_featured: z.boolean().optional().describe("Whether the app is featured"),
  is_hidden: z
    .boolean()
    .optional()
    .describe("Whether the app is hidden from listings"),
  is_invite: z.boolean().optional().describe("Whether the app is invite-only"),
  is_premium: z
    .boolean()
    .optional()
    .describe("Whether the app requires a premium plan"),
  is_public: z
    .boolean()
    .optional()
    .describe("Whether the app is publicly available"),
  is_upcoming: z
    .boolean()
    .optional()
    .describe("Whether the app is upcoming/not yet released"),
  version: z.string().optional().describe("Version string of the app"),
  visibility: z
    .string()
    .optional()
    .describe("Visibility status (e.g., public, private)"),
  actions: z
    .object({
      read: z.number().optional().describe("Number of read actions"),
      read_bulk: z.number().optional().describe("Number of bulk read actions"),
      write: z.number().optional().describe("Number of write actions"),
      search: z.number().optional().describe("Number of search actions"),
      search_or_write: z
        .number()
        .optional()
        .describe("Number of search-or-write actions"),
      search_and_write: z
        .number()
        .optional()
        .describe("Number of search-and-write actions"),
      filter: z.number().optional().describe("Number of filter actions"),
    })
    .optional()
    .describe("Count of available actions by type"),
  description: z.string().optional().describe("Description of the app"),
  primary_color: z.string().optional().describe("Primary brand color (hex)"),
  secondary_color: z
    .string()
    .optional()
    .describe("Secondary brand color (hex)"),
  classification: z.string().optional().describe("App classification category"),
  api_docs_url: z.string().optional().describe("URL to API documentation"),
  image: z.string().optional().describe("Default image URL for the app"),
});

export type ImplementationMeta = z.infer<typeof ImplementationMetaSchema>;

/**
 * Paginated response from /api/v4/implementations-meta/lookup/ and search endpoints
 */
export const ImplementationsMetaResponseSchema = z.object({
  count: z.number().describe("Total number of results"),
  next: z
    .string()
    .nullable()
    .optional()
    .describe("URL for the next page of results"),
  previous: z
    .string()
    .nullable()
    .optional()
    .describe("URL for the previous page of results"),
  results: z
    .array(ImplementationMetaSchema)
    .describe("Array of implementation metadata"),
});

export type ImplementationsMetaResponse = z.infer<
  typeof ImplementationsMetaResponseSchema
>;
